import {
  type Verdict,
  EntityType,
  ExcGetExamCourseVerdictDocument,
  useGetExamCourseVerdict,
  useUpdateOrCreateExamCourseVerdict,
} from '@exam-room/graphql/preview';
import { type PropsWithChildren } from '@shared/utils';
import { createContext, useContext, useMemo } from 'react';

interface ReviewResultContextType {
  verdict: {
    data?: Verdict | null;
    loading: boolean;
  };
  reviewQuestion: (verdict: Verdict) => void;
}

const ReviewResultContext = createContext<ReviewResultContextType | undefined>(undefined);

function useReviewResult() {
  const context = useContext(ReviewResultContext);

  if (!context) throw new Error('useReviewResult must be used within ReviewResultProvider');

  return context;
}

function ReviewResultProvider({
  children,
  questionId,
  taskId,
}: PropsWithChildren & {
  questionId: string;
  taskId: string;
}) {
  const input = {
    entityId: questionId,
    entityType: EntityType.Question,
    taskId,
  };

  const { data, loading } = useGetExamCourseVerdict({
    variables: { getExamCourseVerdictInput: input },
    fetchPolicy: 'cache-and-network',
  });

  const [updateOrCreateExamCourseVerdict, { loading: isReviewing }] =
    useUpdateOrCreateExamCourseVerdict({
      refetchQueries: [
        {
          query: ExcGetExamCourseVerdictDocument,
          variables: { getExamCourseVerdictInput: input },
        },
      ],
    });

  const contextValue = useMemo(
    () => ({
      verdict: {
        data: data?.excGetExamCourseVerdict?.verdict?.verdict,
        loading,
      },
      reviewQuestion: (verdict: Verdict) => {
        if (isReviewing) return;

        updateOrCreateExamCourseVerdict({
          variables: {
            updateOrCreateExamCourseVerdictInput: { ...input, verdict },
          },
        });
      },
    }),
    [data, loading, isReviewing, questionId, taskId],
  );

  return (
    <ReviewResultContext.Provider value={contextValue}>{children}</ReviewResultContext.Provider>
  );
}

export { ReviewResultProvider, useReviewResult };
